import React, {useReducer, ReactNode, useEffect} from 'react'
import {useContextSelector, createContext} from 'use-context-selector'
import {useGetCustomSitePreferences} from '../hooks/useGetCustomSitePreferences'
import {useSyncCioFacetsToUrl} from 'hec/hooks/constructor-io/useSyncCioFacetsToUrl'

type CioFacetOption = {
    status: string
    count: number
    display_name: string
    value: string
    data: object
}

type CioFacets = {
    display_name: string
    name: string
    type: string
    options: CioFacetOption[]
    hidden: boolean
    data: object
}

export type SelectedCioFacets = {
    [category: string]: {value: string; display_name: string}[]
}
// Define the initial state and the reducer
export type CioFacetState = {
    customSitePreferences: object
    selectedCioFacets: SelectedCioFacets
    cioFacets: CioFacets[]
    totalResults: {
        responseTotal: number
        selectedTotal: number
    }
    sortOptions: SortOption[]
    selectedSortOption: SortOption | null
    isInitialized: boolean
}

export type SortOption = {
    sort_by: string
    display_name: string
    sort_order: string
    status?: string
}

const initialState: CioFacetState = {
    customSitePreferences: {},
    selectedCioFacets: {} as SelectedCioFacets,
    cioFacets: [],
    totalResults: {
        responseTotal: 0,
        selectedTotal: 0
    },
    sortOptions: [] as SortOption[],
    selectedSortOption: null,
    isInitialized: false
}

export const SET_CIO_FACETS = 'SET_CIO_FACETS'
export const RESET_CIO_FACETS = 'RESET_CIO_FACETS'
export const SET_CIO_SELECTED_FACETS = 'SET_CIO_SELECTED_FACETS'
export const SET_SELECTED_SORT_OPTION = 'SET_SELECTED_SORT_OPTION'
export const SET_SORT_OPTIONS = 'SET_SORT_OPTIONS'
export const SET_CUSTOM_SITE_PREFERENCES = 'SET_CUSTOM_SITE_PREFERENCES'
export const SET_CIO_FACETS_INITIALIZED = 'SET_CIO_FACETS_INITIALIZED'

export type CioFacetAction =
    | {type: typeof RESET_CIO_FACETS}
    | {type: typeof SET_CIO_FACETS; payload: {facets: CioFacets[]; total_num_results: number}}
    | {type: typeof SET_CIO_SELECTED_FACETS; payload: object}
    | {type: typeof SET_CUSTOM_SITE_PREFERENCES; payload: object}
    | {type: typeof SET_SORT_OPTIONS; payload: SortOption[]}
    | {type: typeof SET_SELECTED_SORT_OPTION; payload: SortOption | null}
    | {type: typeof SET_CIO_FACETS_INITIALIZED}

const CioFacetReducer = (state: CioFacetState, action: CioFacetAction): CioFacetState => {
    switch (action.type) {
        case RESET_CIO_FACETS:
            return {
                ...state,
                selectedCioFacets: {}
            }
        case SET_CIO_FACETS:
            return {
                ...state,
                cioFacets: action.payload.facets,
                totalResults: {
                    responseTotal: action.payload.total_num_results,
                    selectedTotal: state.totalResults.selectedTotal
                }
            }
        case SET_CIO_SELECTED_FACETS:
            return {
                ...state,
                selectedCioFacets: action.payload
            }
        case SET_CUSTOM_SITE_PREFERENCES:
            return {
                ...state,
                customSitePreferences: action.payload
            }
        case SET_SORT_OPTIONS:
            return {
                ...state,
                sortOptions: action.payload
            }
        case SET_SELECTED_SORT_OPTION:
            return {
                ...state,
                selectedSortOption: action.payload
            }
        case SET_CIO_FACETS_INITIALIZED:
            return {
                ...state,
                isInitialized: true
            }
        default:
            return state
    }
}

// Create the context
const CioFacetContext = createContext<{
    state: CioFacetState
    dispatch: React.Dispatch<CioFacetAction>
}>({
    state: initialState,
    dispatch: () => null
})

// Provider component to wrap your app
export const CioFacetProvider: React.FC<{children: ReactNode}> = ({children}) => {
    const {customSitePreferences} = useGetCustomSitePreferences()
    const [state, dispatch] = useReducer(CioFacetReducer, initialState)
    useEffect(() => {
        if (customSitePreferences) {
            dispatch({
                type: SET_CUSTOM_SITE_PREFERENCES,
                payload: customSitePreferences
            })
        }
    }, [customSitePreferences])
    useSyncCioFacetsToUrl(state, dispatch)

    return <CioFacetContext.Provider value={{state, dispatch}}>{children}</CioFacetContext.Provider>
}

// Custom selector hook to use the context
export const useCioFacetSelector = (
    selector: (state: {state: CioFacetState; dispatch: React.Dispatch<CioFacetAction>}) => any
) => {
    const context = useContextSelector(CioFacetContext, (context) => context)
    if (!context) {
        throw new Error('useCioFacetSelector must be used within a CioFacetProvider')
    }
    return useContextSelector(CioFacetContext, selector)
}
